import { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import {
  Area,
  AreaChart,
  Legend,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';

type DailyStat = { date: string; revenue: number; appName?: string };

type Props = {
  dailyStats: DailyStat[];
  exchangeRate?: number;
};

const COLORS = [
  '#0044D2', '#D32F2F', '#1976D2', '#388E3C',
  '#F57C00', '#7B1FA2', '#C2185B', '#0097A7',
];

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('ko-KR', { style: 'currency', currency: 'KRW', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);

export default function AdcashGuruDashboard({ dailyStats, exchangeRate = 1 }: Props) {
  const hasAppBreakdown = dailyStats.some(s => s.appName);

  const appList = useMemo(() => {
    if (!hasAppBreakdown) return [];
    const apps = new Set<string>();
    dailyStats.forEach(s => { if (s.appName) apps.add(s.appName); });
    return Array.from(apps).sort();
  }, [dailyStats, hasAppBreakdown]);

  const chartData = useMemo(() => {
    const allDates = Array.from(new Set(dailyStats.map(s => s.date))).sort();

    return allDates.map(date => {
      const row: Record<string, number | string> = { date };

      if (hasAppBreakdown) {
        let total = 0;
        appList.forEach(app => {
          const rev = dailyStats
            .filter(s => s.date === date && s.appName === app)
            .reduce((sum, s) => sum + s.revenue, 0) * exchangeRate;
          row[app] = rev;
          total += rev;
        });
        if (appList.length > 1) row['전체'] = total;
      } else {
        row['revenue'] = dailyStats
          .filter(s => s.date === date)
          .reduce((sum, s) => sum + s.revenue, 0) * exchangeRate;
      }

      return row;
    });
  }, [dailyStats, appList, exchangeRate, hasAppBreakdown]);

  return (
    <Box sx={{ display: 'flex', gap: 3, mt: 3, minWidth: 0 }}>
      <Box sx={{ border: '1px solid #e0e0e0', backgroundColor: '#ffffff', borderRadius: 2, p: 3, flex: 1, minHeight: 100, minWidth: 0, overflow: 'hidden' }}>
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, textAlign: 'center' }}>
          일별 매출 추이
        </Typography>
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={chartData}>
            <defs>
              {hasAppBreakdown ? (
                <>
                  <linearGradient id="color전체" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#000000" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#000000" stopOpacity={0} />
                  </linearGradient>
                  {appList.map((app, i) => (
                    <linearGradient key={app} id={`color${app.replace(/\s+/g, '')}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={COLORS[i % COLORS.length]} stopOpacity={0.3} />
                      <stop offset="95%" stopColor={COLORS[i % COLORS.length]} stopOpacity={0} />
                    </linearGradient>
                  ))}
                </>
              ) : (
                <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#0044D2" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#0044D2" stopOpacity={0} />
                </linearGradient>
              )}
            </defs>
            <XAxis
              dataKey="date"
              tickFormatter={(v: string) => { const d = new Date(v); return `${d.getMonth() + 1}/${d.getDate()}`; }}
            />
            <YAxis
              tickFormatter={(v: number) => v === 0 ? '0' : `${(v / 1000).toFixed(0)}K`}
            />
            <RechartsTooltip
              formatter={(value: number, name: string) => [formatCurrency(value), name]}
              labelFormatter={(v: string) => new Date(v).toLocaleDateString('ko-KR')}
            />
            {hasAppBreakdown && <Legend />}
            {hasAppBreakdown ? (
              <>
                {appList.length > 1 && (
                  <Area type="monotone" dataKey="전체" stroke="#000000" strokeWidth={3} fillOpacity={0.1} fill="url(#color전체)" name="전체" />
                )}
                {appList.map((app, i) => (
                  <Area
                    key={app}
                    type="monotone"
                    dataKey={app}
                    stroke={COLORS[i % COLORS.length]}
                    strokeWidth={2}
                    fillOpacity={1}
                    fill={`url(#color${app.replace(/\s+/g, '')})`}
                    name={app}
                  />
                ))}
              </>
            ) : (
              <Area type="monotone" dataKey="revenue" stroke="#0044D2" strokeWidth={2} fillOpacity={1} fill="url(#colorRevenue)" name="매출" />
            )}
          </AreaChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
}
